import { DateTime } from 'luxon';
import { IAddNewGenericChecklistRequest, IChecklistEventFormData } from '../../interface/Checklist';
import { ISideEventListFormData } from '../../interface/Checklist';
import { EmailContact } from '../../interface/Obs';
import Colors from '../../style/checklistColor';
import { getDefaultMeetingTime } from '../../helper/dateHelper';

enum EventType {
    DISRUPTIONS = 'DISRUPTIONS',
    TYPHOON = 'TYPHOON',
    GENERIC = 'GENERIC',
    BOMB_THREAT = 'BOMB_THREAT',
    IROPS = 'IROPS',
}

enum CategoryType {
    TYPHOON_HKG = 'Typhoon / HKG',
    TYPHOON_OTHER = 'Typhoon / Others',
    AIRSPACE_DISRUPTION_TPE = 'Airspace Disruption / TPE',
    AIRSPACE_DISRUPTION_OTHERS = 'Airspace Disruption / Others',
    AIR_GROUND_AIRCRAFT_EVACUATION = 'Air / Ground Incident / Aircraft Evacuation',
    AIR_GROUND_STAFF_TURBULENCE_MINOR_INJURIES = 'Air / Ground Incident / Staff / Turbulence Minor Injuries',
    AIR_GROUND_HKG_GROUND_TRANSPORT_INCIDENTS = 'Air / Ground Incident / HKG Ground Transport Incidents',
    IT_SYSTEM_DOWN_DATA_HACK = 'IT System Down / Data Hack',
    IT_SYSTEM_DOWN_IMPACT_OPS_LESS_12HRS = 'IT System Down / Impact Ops < 12hrs',
    IT_SYSTEM_DOWN_IMPACT_OPS_MORE_12HRS = 'IT System Down / Impact Ops + 12hrs',
    SECURITY_ALERT_HKG = 'Security Alert / HKG',
    SECURITY_ALERT_OTHERS = 'Security Alert / Others',
    OPS_DISRUPTION = 'Ops Disruption',
    OFFLINE_PORT_DIVERSION = 'Offline Port Diversion',
    OTHER_EXECUTIVE_CALLED = 'Other-Executive Called',
    PR_ISSUES_POSSIBLE_BRAND_REPUTATION_DAMAGE = 'PR Issues & Possible Brand / Reputation Damage',
    HKG_CATHAY_FACILITIES_EVACUATION_CX_CITY = 'HKG Cathay Facilities Evacuation / CX City (North, Central & South Towers)',
    HKG_CATHAY_FACILITIES_EVACUATION_NON_CX_CITY = 'HKG Cathay Facilities Evacuation / Non-CX City Facilities',
}

const EventTypeIdMap = {
    // TODO: update the id when backend finished
    [EventType.GENERIC]: '669a1a52069c4df5f1fb8da2',
    [EventType.BOMB_THREAT]: '669a1b1b069c4df5f1fb8da4',
    [EventType.IROPS]: '665440bbaefe42890faa0bdb',
    [EventType.TYPHOON]: '662a267935575802ee51821c',
    [EventType.DISRUPTIONS]: '6653df42cd81882c2b952c23',
};

const DetailPageTitleMapping = {
    [EventType.GENERIC]: 'Generic',
    [EventType.BOMB_THREAT]: 'Bomb Threat',
    [EventType.IROPS]: 'Crisis Activation',
    [EventType.TYPHOON]: 'Typhoon',
    [EventType.DISRUPTIONS]: 'Disruptions',
};

enum ChecklistTemplateIds {
    IROPS = '664ffef5896e87a0b184d3fa',
}

enum ChannelType {
    teams = 'Teams',
    whatsapp = 'Whatsapp',
    sms = 'Sms',
    email = 'Email',
    teamsGroup = 'TeamsGroupChat',
}

enum TaskType {
    OBS_MESSAGE = 'OBS_MESSAGE',
    OBS_TEAMS = 'OBS_TEAMS',
    MANUAL = 'MANUAL',
}
enum TaskStatus {
    NOT_STARTED = 'NOT_STARTED',
    IN_PROGRESS = 'IN_PROGRESS',
    BLOCKED = 'BLOCKED',
    COMPLETED = 'COMPLETED',
    CLOSED = 'CLOSED',
}

enum CharacteristicsType {
    itSystem = 'IT_SYSTEM',
    injuries = 'INJURIES',
    weather = 'WEATHER',
    airspace = 'AIRSPACE',
    technical = 'TECHNICAL',
    regulatory = 'REGULATORY',
    political = 'POLITICAL',
    other = 'OTHER',
}

enum SeverityType {
    gold = 'GOLD',
    silver = 'SILVER',
    bronze = 'BRONZE',
}

const createEventTypeList = [
    {
        value: EventType.BOMB_THREAT,
        menuLevel: 0,
        label: 'Bomb Threat',
    },
    {
        value: EventType.IROPS,
        menuLevel: 0,
        label: 'Crisis Activation',
    },
];

interface IEventFormCategory {
    value: string;
    label: string;
    menuLevel: number;
    type?: string;
    nestedOptions?: IEventFormCategory[];
}

const eventFormCategoryList = [
    {
        value: CategoryType.OPS_DISRUPTION,
        label: 'Ops Disruption',
        title: 'Ops Disruption',
        menuLevel: 0,
        type: SeverityType.bronze,
        reminder: {
            title: 'Call GMIOP (HIOC), GMO',
            displaySubTitle: true,
        },
    },
    {
        value: CategoryType.OFFLINE_PORT_DIVERSION,
        label: 'Offline Port Diversion',
        title: 'Offline Port Diversion',
        menuLevel: 0,
        type: SeverityType.bronze,
        reminder: {
            title: 'Call GMIOP (HIOC), GMO',
            displaySubTitle: true,
        },
    },
    {
        value: CategoryType.OTHER_EXECUTIVE_CALLED,
        label: 'Other-Executive Called',
        title: 'Other-Executive Called',
        menuLevel: 0,
        type: SeverityType.bronze,
        reminder: {
            title:
                'Flight Operations related: Call GMIOP (HIOC), GMO\r\n' +
                'Non-Flight Operations related: Call GMIOP (HIOC), GM GSORM (HGS)',
            displaySubTitle: true,
        },
    },
    {
        value: CategoryType.PR_ISSUES_POSSIBLE_BRAND_REPUTATION_DAMAGE,
        label: 'PR Issues & Possible Brand / Reputation Damage',
        title: 'PR Issues & Possible Brand / Reputation Damage',
        menuLevel: 0,
        type: SeverityType.silver,
        reminder: {
            title: 'Call GMIOP (HIOC), GM GSORM (HGS)',
            displaySubTitle: true,
        },
    },
    {
        value: 'Typhoon',
        label: 'Typhoon',
        menuLevel: 0,
        nestedOptions: [
            {
                value: CategoryType.TYPHOON_HKG,
                label: 'HKG',
                title: 'Typhoon',
                menuLevel: 1,
                type: SeverityType.bronze,
                reminder: {
                    title: 'Call GMIOP (HIOC), GMO',
                    displaySubTitle: true,
                },
            },
            {
                value: CategoryType.TYPHOON_OTHER,
                label: 'Others',
                title: 'Typhoon',
                menuLevel: 1,
                type: SeverityType.bronze,
                reminder: {
                    title: 'Call GMIOP (HIOC), GMO',
                    displaySubTitle: true,
                },
            },
        ],
    },
    {
        value: 'Airspace Disruption',
        label: 'Airspace Disruption',
        menuLevel: 0,
        nestedOptions: [
            {
                value: CategoryType.AIRSPACE_DISRUPTION_TPE,
                label: 'TPE',
                title: 'Airspace Disruption',
                menuLevel: 1,
                type: SeverityType.silver,
                reminder: {
                    title: 'Call GMIOP (HIOC), GMO',
                    displaySubTitle: true,
                },
            },
            {
                value: CategoryType.AIRSPACE_DISRUPTION_OTHERS,
                label: 'Others',
                title: 'Airspace Disruption',
                menuLevel: 1,
                type: SeverityType.bronze,
                reminder: {
                    title: 'Call GMIOP (HIOC), GMO',
                    displaySubTitle: true,
                },
            },
        ],
    },
    {
        value: 'Air / Ground Incident',
        label: 'Air / Ground Incident',
        menuLevel: 0,
        nestedOptions: [
            {
                value: CategoryType.AIR_GROUND_AIRCRAFT_EVACUATION,
                label: 'Aircraft Evacuation',
                title: 'Air / Ground Incident',
                menuLevel: 1,
                type: SeverityType.silver,
                reminder: {
                    title: 'Call GMIOP (HIOC), GMO',
                    displaySubTitle: true,
                },
            },
            {
                value: CategoryType.AIR_GROUND_STAFF_TURBULENCE_MINOR_INJURIES,
                label: 'Staff / Turbulence Minor Injuries',
                title: 'Air / Ground Incident',
                menuLevel: 1,
                type: SeverityType.silver,
                reminder: {
                    title:
                        'Flight Operations related: Call GMIOP (HIOC), GMO\r\n' +
                        'Non-Flight Operations related: Call GMIOP (HIOC), GM GSORM (HGS)',
                    displaySubTitle: true,
                },
            },
            {
                value: CategoryType.AIR_GROUND_HKG_GROUND_TRANSPORT_INCIDENTS,
                label: 'HKG Ground Transport Incidents',
                title: 'Air / Ground Incident',
                menuLevel: 1,
                type: SeverityType.bronze,
                reminder: {
                    title: 'Call GMIOP (HIOC), GM GSORM (HGS)',
                    displaySubTitle: true,
                },
            },
        ],
    },
    {
        value: 'IT System Down',
        label: 'IT System Down',
        menuLevel: 0,
        nestedOptions: [
            {
                value: CategoryType.IT_SYSTEM_DOWN_DATA_HACK,
                label: 'Data Hack',
                title: 'IT System Down',
                menuLevel: 1,
                type: SeverityType.silver,
                reminder: {
                    title: 'Call GMIOP (HIOC), GM GSORM (HGS)',
                    displaySubTitle: true,
                },
            },
            {
                value: CategoryType.IT_SYSTEM_DOWN_IMPACT_OPS_LESS_12HRS,
                label: 'Impact Ops < 12hrs',
                title: 'IT System Down',
                menuLevel: 1,
                type: SeverityType.bronze,
                reminder: {
                    title:
                        'Flight Operations related: Call GMIOP (HIOC), GMO\r\n' +
                        'Non-Flight Operations related: Call GMIOP (HIOC), GM GSORM (HGS)',
                    displaySubTitle: true,
                },
            },
            {
                value: CategoryType.IT_SYSTEM_DOWN_IMPACT_OPS_MORE_12HRS,
                label: 'Impact Ops + 12hrs',
                title: 'IT System Down',
                menuLevel: 1,
                type: SeverityType.silver,
                reminder: {
                    title:
                        'Flight Operations related: Call GMIOP (HIOC), GMO\r\n' +
                        'Non-Flight Operations related: Call GMIOP (HIOC), GM GSORM (HGS)',
                    displaySubTitle: true,
                },
            },
        ],
    },
    {
        value: 'Security Alert',
        label: 'Security Alert',
        menuLevel: 0,
        nestedOptions: [
            {
                value: CategoryType.SECURITY_ALERT_HKG,
                label: 'HKG',
                title: 'Security Alert',
                menuLevel: 1,
                type: SeverityType.silver,
                reminder: {
                    title: 'Call GMIOP (HIOC), GM GSORM (HGS)',
                    displaySubTitle: true,
                },
            },
            {
                value: CategoryType.SECURITY_ALERT_OTHERS,
                label: 'Others',
                title: 'Security Alert',
                menuLevel: 1,
                type: SeverityType.bronze,
                reminder: {
                    title: 'Call GMIOP (HIOC), GM GSORM (HGS)',
                    displaySubTitle: true,
                },
            },
        ],
    },
    {
        value: 'HKG Cathay Facilities Evacuation',
        label: 'HKG Cathay Facilities Evacuation',
        menuLevel: 0,
        nestedOptions: [
            {
                value: CategoryType.HKG_CATHAY_FACILITIES_EVACUATION_CX_CITY,
                label: 'CX City (North, Central & South Towers)',
                title: 'HKG Cathay Facilities Evacuation',
                menuLevel: 1,
                type: SeverityType.bronze,
                reminder: {
                    title: 'Consultation call to GMIOP & GM GSORM is not required unless severity level escalation is deemed necessary',
                    displaySubTitle: false,
                },
            },
            {
                value: CategoryType.HKG_CATHAY_FACILITIES_EVACUATION_NON_CX_CITY,
                label: 'Non-CX City Facilities',
                title: 'HKG Cathay Facilities Evacuation',
                menuLevel: 1,
                type: SeverityType.bronze,
                reminder: {
                    title: 'Call GMIOP (HIOC), GM GSORM (HGS)',
                    displaySubTitle: true,
                },
            },
        ],
    },
];

const getCategoryKeyByValue = (list: IEventFormCategory[], value, key) => {
    for (const item of list) {
        if (item.value === value) {
            return item[key];
        }
        if (item.nestedOptions) {
            const foundType = getCategoryKeyByValue(item.nestedOptions, value, key);
            if (foundType) {
                return foundType;
            }
        }
    }
    return '';
};

const characteristicsList = [
    { label: 'IT System', value: CharacteristicsType.itSystem },
    { label: 'Injuries', value: CharacteristicsType.injuries },
    { label: 'Weather', value: CharacteristicsType.weather },
    { label: 'Airspace', value: CharacteristicsType.airspace },
    { label: 'Technical', value: CharacteristicsType.technical },
    { label: 'Regulatory', value: CharacteristicsType.regulatory },
    { label: 'Political', value: CharacteristicsType.political },
    { label: 'Other', value: CharacteristicsType.other },
];

const severityTypeList = [
    { label: 'Bronze', value: SeverityType.bronze },
    { label: 'Silver', value: SeverityType.silver },
];

const checklists = [
    { label: 'Typhoon Preparation', value: '6645922053c2a14571ca47af' },
    { label: 'Typhoon Escalation', value: '664ac993d87c5d25882c8d97' },
    { label: 'Typhoon Disruption Recovery', value: '664aca21d87c5d25882c8d9a' },
];

const statusList = [
    { display: 'In progress', value: TaskStatus.IN_PROGRESS },
    { display: 'Blocked', value: TaskStatus.BLOCKED },
    { display: 'Completed', value: TaskStatus.COMPLETED },
    { display: 'Not started', value: TaskStatus.NOT_STARTED },
    { display: 'Closed', value: TaskStatus.CLOSED },
];

const statusBgColorMapping = {
    [TaskStatus.IN_PROGRESS]: Colors.Status.inProgress,
    [TaskStatus.BLOCKED]: Colors.Status.blocked,
    [TaskStatus.COMPLETED]: Colors.Status.completed,
    [TaskStatus.NOT_STARTED]: Colors.Status.notStart,
    [TaskStatus.CLOSED]: Colors.Status.closed,
};

const FORM_WIDTH = '500px';
const CREATE_FORM_WIDTH = '564px';

const openStateChecklistIdPrefix = 'checklist:';
const openStateTaskIdPrefix = 'task:';

enum eventFormFieldName {
    eventTypeId = 'eventTypeId',
    eventType = 'eventType',
    eventTitle = 'eventTitle',
    eventDescription = 'eventDescription',
    checklistTemplateIds = 'checklistTemplateIds',
}

enum IROPSFieldName {
    networkImpact = 'eventDetails.networkImpact',
    characteristic = 'eventDetails.characteristic',
    location = 'eventDetails.location',
    port = 'eventDetails.port',
    severity = 'eventDetails.severity',
    attachments = 'eventDetails.attachments',
    category = 'eventDetails.category',
    onlyExercise = 'eventDetails.onlyExercise',
    actionLogs = 'actionLogs',
    start = 'meeting.start',
    end = 'meeting.end',
    recipients = 'meeting.recipients',
    checklistGroups = 'meeting.checklistGroups',
    timezone = 'meeting.eventOption.timezone',
    allowNewTimeProposals = 'meeting.eventOption.allowNewTimeProposals',
    isReminderOn = 'meeting.eventOption.isReminderOn',
    isAllDay = 'meeting.eventOption.isAllDay',
    displayName = 'meeting.eventOption.location.displayName',
    subject = 'meeting.subject',
    content = 'meeting.content',
    channels = 'meeting.channels',
    props = 'meeting.props',
    selectedChecklistTemplateId = 'meeting.selectedChecklistTemplateId',
}

enum BombThreatFieldName {
    flightOrPort = 'eventDetails.flightOrPort',
    attachments = 'eventDetails.attachments',
}

enum TaskFormFieldName {
    taskName = 'taskName',
    assignTo = 'assignTo',
    dueDatetime = 'dueDatetime',
    taskDescription = 'taskDescription',
    subject = 'taskFields.subject',
    recipients = 'taskFields.recipients',
    content = 'taskFields.content',
    details = 'taskFields.details',
    end = 'taskFields.end',
    start = 'taskFields.start',
    mediums = 'taskFields.mediums',
}

enum ChecklistFormFieldName {
    selectedChecklistTemplateId = 'selectedChecklistTemplateId',
    checklistName = 'checklistName',
    checklistDescription = 'checklistDescription',
    selectedTaskTemplateIds = 'selectedTaskTemplateIds',
}

enum SideEventListFieldName {
    eventType = 'eventType',
    eventStatus = 'eventStatus',
    start = 'start',
    end = 'end',
}

enum EmailType {
    group = 'group',
    member = 'member',
    other = 'other',
}

const updateTaskBannerText = 'Task successfully saved';
const sendMeetingBannerText = 'Teams meeting successfully sent';
const sendMessageBannerText = 'Message successfully sent';

const checklistFormBaseDefaultValues: IAddNewGenericChecklistRequest = {
    eventId: '',
};

const eventFormBaseDefaultValues: IChecklistEventFormData = {
    eventType: '',
    eventTitle: '',
};

const eventFormGenericDefaultValues = (eventType: EventType) => {
    const defaultValues: IChecklistEventFormData = {
        eventTypeId: EventTypeIdMap[eventType] || '',
        eventType: eventType || '',
        eventTitle: '',
        eventDescription: '',
        checklistTemplateIds: [],
    };
    return defaultValues;
};

const IROPSDefaultValues = {
    checklistTemplateIds: ['664ffef5896e87a0b184d3fa'],
    eventMeetingLocationDefaultValue: 'IOC Meeting Room',
};

const eventFormIROPsDefaultValues = (eventType: EventType, recipients: string[]) => {
    const { start, end } = getDefaultMeetingTime();
    const defaultValues: IChecklistEventFormData = {
        eventTypeId: EventTypeIdMap[eventType] || '',
        checklistTemplateIds: IROPSDefaultValues.checklistTemplateIds,
        eventType,
        eventTitle: '',
        eventDescription: '',
        eventDetails: {
            characteristic: CharacteristicsType.other,
            networkImpact: '',
            location: '',
            category: '',
            severity: '',
            onlyExercise: false,
            attachments: [],
        },

        meeting: {
            recipients: recipients.map((recipient) => ({ value: recipient, name: recipient })),
            // checklistGroups,
            start,
            end,
            channels: [ChannelType.teamsGroup],
            eventOption: {
                timezone: 'HKT',
                isAllDay: false,
                location: {
                    displayName: IROPSDefaultValues.eventMeetingLocationDefaultValue,
                },
            },
        },
    };
    return defaultValues;
};

const defaultTeamMeetingGroup = 'DL_CX_IOC_BW_Group_CX@cathaypacific.com';

const teamMeetingAndChatDefaultValues = (fixRecipients?: EmailContact[]) => {
    const { start, end } = getDefaultMeetingTime();
    return {
        recipients: [
            ...(fixRecipients ?? []),
            { name: defaultTeamMeetingGroup, email: defaultTeamMeetingGroup, type: EmailType.group },
        ],
        content: { textContent: '', htmlContent: '' },
        end,
        start,
    };
};

const eventFormBombThreatDefaultValues = (eventType: EventType) => {
    const defaultValues: IChecklistEventFormData = {
        eventTypeId: EventTypeIdMap[eventType] || '',
        eventType,
        eventTitle: '',
        eventDescription: '',
        eventDetails: {
            ufi: '',
            flightOrPort: {},
            arrivalPort: '',
            departurePort: '',
            attachments: [],
        },
    };
    return defaultValues;
};

const teamMeetingDefaultValues = (isNewMeeting: boolean) => {
    const { start, end } = getDefaultMeetingTime();
    const defaultValues: IChecklistEventFormData = {
        meeting: {
            subject: isNewMeeting ? '' : 'Meeting 1',
            recipients: [],
            checklistGroups: [],
            content: {
                htmlContent: '',
                textContent: '',
            },
            end,
            start,
            selectedChecklistTemplateId: '',
            ...(isNewMeeting
                ? {
                      eventOption: {
                          timezone: 'HKT',
                          allowNewTimeProposals: true,
                          isReminderOn: true,
                          isAllDay: false,
                          location: {
                              displayName: '',
                          },
                      },
                  }
                : {}),
        },
    };
    return defaultValues;
};

enum CommentsItemMenuText {
    editComment = 'Edit comment',
    deleteComment = 'Delete comment',
}

enum SequenceType {
    checklist = 'checklist',
    task = 'task',
}

enum isRequireMeeting {
    YES = 'YES',
    NO = 'NO',
}

enum EventStatus {
    CLOSED = 'CLOSED',
    OPEN = 'OPEN',
}

enum isSelectSetupMeeting {
    YES = 'YES',
    NO = 'NO',
}
enum threatAssessmentLevel {
    RED = 'RED',
    AMBER = 'AMBER',
    GREEN = 'GREEN',
}
enum isChinaFlight {
    YES = 'YES',
    NO = 'NO',
}
enum noMeetingThreatAssessmentLevel {
    GREEN = 'GREEN',
    NONE = 'NONE',
}

const isRequireMeetingMapping = {
    [isRequireMeeting.YES]: 'Yes, BTAG meeting is required',
    [isRequireMeeting.NO]: 'No, BTAG meeting is not required',
};
const isSelectSetupMeetingMapping = {
    [isSelectSetupMeeting.YES]: 'yes',
    [isSelectSetupMeeting.NO]: 'no',
};

const threatAssessmentLevelMapping = {
    [threatAssessmentLevel.RED]: 'Red',
    [threatAssessmentLevel.AMBER]: 'Amber',
    [threatAssessmentLevel.GREEN]: 'Green',
};

const isChinaFlightMapping = {
    [isChinaFlight.YES]: 'Yes',
    [isChinaFlight.NO]: 'No',
};

const noMeetingThreatAssessmentLevelMapping = {
    [noMeetingThreatAssessmentLevel.GREEN]: 'Green',
    [noMeetingThreatAssessmentLevel.NONE]: 'None',
};

const bombThreatTemplateIds = {
    teamsChatMeeting: '66c31b0b1c6056a430fb8dd0',
    teamsChatMeetingRedLevel: '66c31b0b1c6056a430fb8dd1',
    teamsChatMeetingAmberLevel: '66c31b0b1c6056a430fb8dd2',
    teamsChatMeetingGreenChinaFlightLevel: '66c31b0b1c6056a430fb8dd3',
    teamsChatMeetingGreenNotChinaFlightLevel: '66c31b0b1c6056a430fb8dd4',
    greenChinaFlightLevel: '66c31b0b1c6056a430fb8dd5',
    greenNotChinaFlightLevel: '66c31b0b1c6056a430fb8dd6',
    noneLevel: '66c31b0b1c6056a430fb8dd7',
};

const ChinaFlightStatus = {
    YES: 1,
    NO: 0,
    UNDEFINED: -1,
};

const removeBombThreatLevelTemplateList = [
    bombThreatTemplateIds.teamsChatMeetingRedLevel,
    bombThreatTemplateIds.teamsChatMeetingAmberLevel,
    bombThreatTemplateIds.teamsChatMeetingGreenChinaFlightLevel,
    bombThreatTemplateIds.teamsChatMeetingGreenNotChinaFlightLevel,
    bombThreatTemplateIds.greenChinaFlightLevel,
    bombThreatTemplateIds.greenNotChinaFlightLevel,
    bombThreatTemplateIds.noneLevel,
];

const bombThreatQuestionObject = {
    isRequireMeeting: [isRequireMeetingMapping.YES, isRequireMeetingMapping.NO],
    threatAssessmentLevel: [
        threatAssessmentLevelMapping.RED,
        threatAssessmentLevelMapping.AMBER,
        threatAssessmentLevelMapping.GREEN,
    ],
    isChinaFlight: [isChinaFlightMapping.YES, isChinaFlightMapping.NO],
    noMeetingThreatAssessmentLevel: [
        noMeetingThreatAssessmentLevelMapping.GREEN,
        noMeetingThreatAssessmentLevelMapping.NONE,
    ],
};

const IROPSSeverityType = {
    Bronze: 'Bronze',
    Silver: 'Silver',
};

const IROPSSeverityTypeList = {
    [SeverityType.silver]: IROPSSeverityType.Silver,
    [SeverityType.bronze]: IROPSSeverityType.Bronze,
};

const eventStatusList = [
    {
        value: EventStatus.OPEN,
        menuLevel: 0,
        label: 'Open',
    },
    {
        value: EventStatus.CLOSED,
        menuLevel: 0,
        label: 'Closed',
    },
];

const SideEventListFiltersDefaultValues: ISideEventListFormData = {
    eventType: '',
    eventStatus: EventStatus.OPEN,
    start: DateTime.now().minus({ months: 2 }).startOf('day').toUTC().toISO(),
    end: DateTime.now().endOf('day').toUTC().toISO(),
};

const RECIPIENT_GROUPS = [
    'IOC',
    'FOP',
    'ISD',
    'ASD',
    'CCD',
    'IT',
    'CGO',
    'CAF',
    'REV',
    'ENG',
    'FIN',
    'HKIA',
    'PLN',
    'GSORM',
];
export {
    openStateTaskIdPrefix,
    openStateChecklistIdPrefix,
    EventType,
    TaskStatus,
    TaskType,
    ChecklistTemplateIds,
    ChannelType,
    IROPSFieldName,
    BombThreatFieldName,
    FORM_WIDTH,
    CREATE_FORM_WIDTH,
    checklists,
    statusList,
    statusBgColorMapping,
    characteristicsList,
    severityTypeList,
    CharacteristicsType,
    SeverityType,
    DetailPageTitleMapping,
    TaskFormFieldName,
    updateTaskBannerText,
    sendMeetingBannerText,
    sendMessageBannerText,
    EmailType,
    eventFormFieldName,
    createEventTypeList,
    eventFormBaseDefaultValues,
    eventFormGenericDefaultValues,
    eventFormBombThreatDefaultValues,
    teamMeetingDefaultValues,
    ChecklistFormFieldName,
    checklistFormBaseDefaultValues,
    CommentsItemMenuText,
    SequenceType,
    teamMeetingAndChatDefaultValues,
    isRequireMeetingMapping,
    isSelectSetupMeetingMapping,
    threatAssessmentLevelMapping,
    isChinaFlightMapping,
    noMeetingThreatAssessmentLevelMapping,
    bombThreatTemplateIds,
    defaultTeamMeetingGroup,
    ChinaFlightStatus,
    removeBombThreatLevelTemplateList,
    bombThreatQuestionObject,
    eventFormCategoryList,
    IROPSSeverityType,
    getCategoryKeyByValue,
    eventFormIROPsDefaultValues,
    IROPSDefaultValues,
    IROPSSeverityTypeList,
    SideEventListFieldName,
    eventStatusList,
    EventStatus,
    SideEventListFiltersDefaultValues,
    EventTypeIdMap,
    RECIPIENT_GROUPS,
    CategoryType,
};
